import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import FavoriteBorderIcon from "@material-ui/icons/FavoriteBorder";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import RemoveIcon from "@material-ui/icons/HighlightOff";
import CheckIcon from "@material-ui/icons/CheckCircleOutline";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import HealthInformationTabs from "../../../Athletes/components/PatientProfile/components/HealthInformationTabs";
import EditIcon from "@material-ui/icons/Edit";
import { getOrgDoctorsRequest } from "../../../Doctors/DoctorApiActions";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from "@material-ui/lab";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import { getUserInfo } from "../../../../utilities/storage";
import {
  addLogItemRequest,
  getCaseByIdRequest,
  getCaseStatusForDoctorRequest,
  getCaseStatusRequest,
  getDoctorsOfDeptRequest,
  getLogItemsByCaseIdRequest,
  getSignedUrlRequest,
  updateCaseDoctorsRequest,
  updateCaseRequest,
  uploadToS3Request,
} from "../../CasesApiAction";
import { useStyles } from "./styles";
import CaseCloseConfirmationDialog from "./components/CaseCloseConfirmationDialog/CaseCloseConfirmationDialog";
import MessageDialog from "../../../../utilities/components/MessageDialog/MessageDialog";
import {
  APP_URLS,
  ORGANIZATION_TYPES,
  ROLES,
} from "../../../../utilities/constants";
import PatientCasesDialog from "../PatientCasesDialog/PatientCasesDialog";
import PrescriptionForm from "../../../Prescription/components/PatientForm/PrescriptionForm";
import PrescriptionList from "../../../Prescription/components/PrescriptionList/PrescriptionList";

// BMI from the same user-report-individual-detail row that supplies the height
// and weight above. Mirrors bmiFor() in the Strapi health-insights lib
// (src/api/health-insights/lib/individual-details.js) so both read a patient
// identically: kg / m2, and null rather than a number for missing, non-numeric
// or non-positive inputs.
const bmiFor = (weightKg, heightCms) => {
  const weight = Number(weightKg);
  const height = Number(heightCms);
  if (!Number.isFinite(weight) || !Number.isFinite(height)) return null;
  if (weight <= 0 || height <= 0) return null;
  const metres = height / 100;
  const bmi = weight / (metres * metres);
  return Number.isFinite(bmi) ? Math.round(bmi * 10) / 10 : null;
};

/**
 * function to render case form
 * @param history: history from router
 * @param selectedDept: selected department
 * @param getCaseById: function to get case by id
 * @param singleCase: case details
 * @param updateCase: function to update case
 * @param getCaseStatus: function to get case status
 * @param getCaseStatusForDoctor: function to get case status for health provider organization departments
 * @param caseStatus: case status list
 * @param getDoctorsOfOrg: function to get doctors of departments
 * @param getLogItemsByCaseId: function to get log items by case id
 * @param logItems: log items list
 * @param addLogItem: function to add log item
 * @param updateCaseDoctors: function to update case doctors
 * @param getSignedUrl: function to get signed url
 * @param uploadToS3: function to upload file on signed url
 * @returns {JSX.Element}
 * @constructor
 */
const Case = ({
  history,
  selectedDept,
  getCaseById,
  singleCase,
  updateCase,
  getCaseStatus,
  getCaseStatusForDoctor,
  caseStatus,
  getDoctorsOfOrg,
  getLogItemsByCaseId,
  logItems,
  updateCaseDoctors,
}) => {
  const classes = useStyles();
  const role = getUserInfo().Roles[0].name;
  const params = useParams();
  const [status, setStatus] = useState(null);
  const [isCritical, setIsCritical] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [fixedDoctor, setFixedDoctor] = useState([]);
  const [description, setDescription] = useState("");
  const [showEditDescriptionBox, setShowEditDescriptionBox] = useState(false);
  // Open directly on the Health Information view (its default sub-tab is the
  // Overview), giving the doctor an immediate clinical snapshot. The SOAP
  // ("Notes") view has been removed.
  const [sortCategory, setSortCategory] = useState("healthInformation");

  const [doctorsDataList, setDoctorsDataList] = useState(null);
  const [openCloseCaseConfirmation, setOpenCloseCaseConfirmation] =
    useState(false);
  const [closeState, setCloseState] = useState(null);
  const [openCloseCaseMessage, setOpenCloseCaseMessage] = useState(false);
  const [openDoctorList, setOpenDoctorList] = useState(false);
  const [editableDoctors, setEditableDoctors] = useState([]);
  const [isOpenPrescriptionForm, setIsOpenPrescriptionForm] = useState(false);
  const [isGoBackClicked, setIsGoBackClicked] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [valueHealthHistory, setValueHealthHistory] = React.useState("1");
  const [openCasesList, setOpenCasesList] = useState(false);
  const [formKey, setFormKey] = useState(Date.now());

  useEffect(() => {
    getCaseById(params.id).then((res) => {
      if (
        selectedDept.OrganizationType === ORGANIZATION_TYPES.HEALTHCARE_PROVIDER
      ) {
        getCaseStatusForDoctor().then((resStatus) => {
          const currentStatus = resStatus.find(
            (status) => status.id === res.status
          );
          setStatus(currentStatus);
        });
      } else {
        getCaseStatus().then((resStatus) => {
          const currentStatus = resStatus.find(
            (status) => status.id === res.status
          );
          setStatus(currentStatus);
        });
      }

      if (res.priority === 0) {
        setIsCritical(false);
      } else if (res.priority) {
        setIsCritical(true);
      }

      setDescription(res.desc);

      const filterdDoctors = res.Users.filter((user) => {
        return (
          user.userId === getUserInfo().id ||
          user.userId === res.createdByUser.userId
        );
      });

      setFixedDoctor(filterdDoctors);
      setDoctors(res.Users);

      const loggedInDoctor = res.Users.find(
        (doctor) => doctor.userId === getUserInfo().id
      );

      getDoctorsOfOrg().then((careRes) => {
        if (!loggedInDoctor) {
          const doctorsWithoutLoggedInUser = careRes.rows.filter(
            (doctor) => doctor.userId !== getUserInfo().id
          );

          setDoctorsDataList(doctorsWithoutLoggedInUser);
        } else {
          setDoctorsDataList(careRes.rows);
        }
      });
    });
  }, [
    params.id,
    getCaseById,
    getDoctorsOfOrg,
    getCaseStatusForDoctor,
    getCaseStatus,
  ]);

  useEffect(() => {
    if (sortCategory !== "prescription") {
      getLogItemsByCaseId(params.id, sortCategory).then((res) => {});
    }
  }, [getLogItemsByCaseId, params.id, sortCategory]);

  /**
   * function to render doctor chip
   * @param props: doctor details
   * @returns {JSX.Element}
   * @constructor
   */
  const DoctorChip = (props) => {
    return (
      <Chip avatar={<Avatar alt="profile" src={props.url} />} {...props} />
    );
  };

  /**
   * function to handle edit description
   * @param e: e from button click
   */
  const onDescriptionEdit = (e) => {
    e.preventDefault();
    const data = {
      desc: description,
    };

    updateCase(params.id, data).then((res) => {
      setShowEditDescriptionBox(false);
    });
  };

  /**
   * function to handle case status change
   * @param e: e from button click
   * @param value: selected case status
   */
  const onStatusChange = (e, value) => {
    if (
      value.description === "Closed" &&
      singleCase &&
      singleCase.priority === 1
    ) {
      setCloseState(value);
      setOpenCloseCaseConfirmation(true);
    } else {
      setStatus(value);

      const data = {
        status: value.id,
      };
      updateCase(params.id, data).then((res) => {});
    }
  };

  /**
   * function to handle closing of case
   */
  const handleCloseCase = () => {
    const data = {
      status: closeState.id,
    };
    updateCase(params.id, data).then((res) => {
      setStatus(closeState);

      if (res.priority === 0) {
        setIsCritical(false);
      } else if (res.priority) {
        setIsCritical(true);
      }

      setOpenCloseCaseConfirmation(false);
    });
  };

  /**
   * function to close confirmation dialog for closing a urgent close
   */
  const handleConfirmationClose = () => {
    setOpenCloseCaseConfirmation(false);
  };

  /**
   * function to handle case priority change
   * @param e: e from button click
   */
  const onPriorityChange = (e) => {
    if (status && status.id === 5) {
      setOpenCloseCaseMessage(true);
    } else {
      if (e.target.checked) {
        const data = {
          priority: "1",
        };

        updateCase(params.id, data).then((res) => {});
      }
      if (!e.target.checked) {
        const data = {
          priority: "0",
        };

        updateCase(params.id, data).then((res) => {});
      }
      setIsCritical(e.target.checked);
    }
  };
  /**
   * function to build initials for the patient avatar fallback
   * @param name: full patient name
   * @returns {string} up to two uppercase initials
   */
  const getInitials = (name) => {
    if (!name) return "";
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
  };

  const formatToIST = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  /**
   * function to handle doctors list change
   */
  const handleDoctorsList = () => {
    const data = {
      doctorId: editableDoctors.map((doctor) => doctor.userId),
    };

    updateCaseDoctors(params.id, data).then((res) => {
      getLogItemsByCaseId(params.id, sortCategory).then((res) => {
        getCaseById(params.id).then((res) => {
          setDoctors(res.Users);
        });
        setOpenDoctorList(false);
      });
    });
  };

  /**
   * function to handle go back
   */
  const onBack = () => {
    history.goBack();
  };

  const handleChangeHealthHistory = (e, newValue) => {
    setValueHealthHistory(newValue);
  };

  /**
   * function to switch to another case of the same patient
   * @param caseItem: case selected from the patient cases dialog
   */
  const handleCaseSelect = (caseItem) => {
    setOpenCasesList(false);
    if (caseItem.id === singleCase.id) return;

    setIsOpenPrescriptionForm(false);
    setSelectedPrescription(null);
    setSortCategory("healthInformation");
    history.push(`${APP_URLS.CASES}/${caseItem.id}`);
  };

  return (
    <>
      <Box className={classes.container}>
        <Box component={Paper} className={classes.actionsContainer}>
          <Box>
            <Button
              startIcon={<ArrowBackIosIcon />}
              className={classes.backButton}
              variant="outlined"
              color="primary"
              disableElevation
              onClick={onBack}
            >
              Back
            </Button>
          </Box>

          {role !== ROLES.ROLE_ORG_ADMIN && role !== ROLES.ROLE_RECEPTIONIST ? (
            <Box className={classes.buttonsInnerContainer}>
              <Box className={classes.criticalContainer}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={isCritical}
                      onChange={onPriorityChange}
                    />
                  }
                  label="Urgent"
                  labelPlacement="end"
                />
              </Box>

              <Box className={classes.selectContainer}>
                <Autocomplete
                  disableClearable
                  fullWidth
                  size="small"
                  options={caseStatus ? caseStatus : []}
                  getOptionSelected={(option, value) => {
                    if (option && value) {
                      return option.id === value.id;
                    } else {
                      return true;
                    }
                  }}
                  getOptionLabel={(option) => {
                    return (
                      option.description.charAt(0).toUpperCase() +
                      option.description.slice(1)
                    );
                  }}
                  value={status}
                  onChange={onStatusChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Case Status"
                      variant="outlined"
                    />
                  )}
                />
              </Box>
            </Box>
          ) : (
            <></>
          )}
        </Box>

        {singleCase && (
          <>
            <Box mt={2} component={Paper} p={3} m={2} className={classes.headerCard}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Box className={classes.profileContainer}>
                    <Box>
                      <Avatar
                        alt="profile"
                        src={
                          singleCase.Patient && singleCase.Patient.imageUrl
                            ? singleCase.Patient.imageUrl
                            : undefined
                        }
                        className={clsx(
                          classes.profileAvatar,
                          isCritical && classes.criticalAvatar
                        )}
                      >
                        {getInitials(
                          singleCase.externalReport?.attributes?.name ??
                            (singleCase.Patient
                              ? `${singleCase.Patient.firstname} ${singleCase.Patient.lastname}`
                              : "")
                        )}
                      </Avatar>
                    </Box>
                    <Box className={classes.profileInfoContainer}>
                      <Box className={classes.caseInfoContainer}>
                        <Box>
                          {singleCase.Patient && (
                            <Typography
                              variant="h3"
                              className={clsx(
                                classes.name,
                                isCritical && classes.criticalName
                              )}
                            >
                              {singleCase.externalReport?.attributes?.name ??
                                `${singleCase.Patient.firstname} ${singleCase.Patient.lastname}`}

                              {role === ROLES.ROLE_ORG_ADMIN && (
                                <span className={classes.text}>
                                  {status &&
                                    `(${
                                      status.description
                                        .charAt(0)
                                        .toUpperCase() +
                                      status.description.slice(1)
                                    }${
                                      isCritical === true
                                        ? ", Urgent"
                                        : ", Normal"
                                    })`}
                                </span>
                              )}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box className={classes.createdByContainer}>
                        {singleCase.createdByUser && (
                          <Typography className={classes.createdByText}>
                            {`Created by ${
                              singleCase.createdByUser.firstName
                            } ${
                              singleCase.createdByUser.lastName
                            },  ${formatToIST(singleCase.createdAt)}`}
                          </Typography>
                        )}
                      </Box>
                      {showEditDescriptionBox ? (
                        <Box className={classes.editBoxContainer}>
                          <ValidatorForm
                            onSubmit={onDescriptionEdit}
                            className={classes.formContainer}
                          >
                            <TextValidator
                              fullWidth
                              label="Description"
                              size="small"
                              variant="outlined"
                              multiline
                              rows={5}
                              rowsMax={5}
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                              validators={["required"]}
                              errorMessages={["Description is required"]}
                            />

                            <Box className={classes.editBoxButtonContainer}>
                              <Box>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  disableElevation={true}
                                  onClick={() => {
                                    setShowEditDescriptionBox(false);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                              <Box className={classes.editBoxSaveButton}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  disableElevation={true}
                                  type="submit"
                                >
                                  Save
                                </Button>
                              </Box>
                            </Box>
                          </ValidatorForm>
                        </Box>
                      ) : (
                        <Box>
                          <Box className={classes.descContainer}>
                            <Box mb={role !== ROLES.ROLE_ORG_ADMIN ? 0 : 1}>
                              <Typography className={classes.descLabel}>
                                Description
                              </Typography>
                            </Box>
                            {role !== ROLES.ROLE_ORG_ADMIN &&
                            role !== ROLES.ROLE_RECEPTIONIST ? (
                              <IconButton
                                color="primary"
                                onClick={() => {
                                  setShowEditDescriptionBox(true);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            ) : (
                              <></>
                            )}
                          </Box>
                          <Box>
                            <Typography className={classes.descText}>
                              {singleCase.desc}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {!openDoctorList ? (
                        <Box>
                          <Box className={classes.descContainer}>
                            <Box mb={role !== ROLES.ROLE_ORG_ADMIN ? 0 : 1}>
                              <Typography className={classes.descLabel}>
                                Doctors
                              </Typography>
                            </Box>
                            {role !== ROLES.ROLE_ORG_ADMIN &&
                            role !== ROLES.ROLE_RECEPTIONIST ? (
                              <IconButton
                                color="primary"
                                onClick={() => {
                                  if (status && status.id === 5) {
                                    setOpenCloseCaseMessage(true);
                                  } else {
                                    setOpenDoctorList(true);
                                    setEditableDoctors([...doctors]);
                                  }
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            ) : (
                              <></>
                            )}
                          </Box>

                          {doctors &&
                            doctors.map((option, index) => (
                              <DoctorChip
                                key={index}
                                variant="outlined"
                                label={`${option.firstName} ${option.lastName}`}
                                url={option.imageUrl}
                                color={
                                  option.userId ===
                                    singleCase.createdByUser.userId ||
                                  option.userId === getUserInfo().id
                                    ? "primary"
                                    : ""
                                }
                                deleteIcon={
                                  option.userId ===
                                    singleCase.createdByUser.userId ||
                                  option.userId === getUserInfo().id ? (
                                    <CheckIcon />
                                  ) : (
                                    <RemoveIcon />
                                  )
                                }
                                className={classes.doctorChip}
                              />
                            ))}
                        </Box>
                      ) : (
                        <Box className={classes.doctorsListContainer}>
                          <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={12} sm={12} md={9} lg={9} xl={9}>
                              <Autocomplete
                                popupIcon={null}
                                disableClearable
                                filterSelectedOptions
                                fullWidth
                                size="small"
                                multiple
                                options={doctorsDataList ? doctorsDataList : []}
                                value={[
                                  ...new Map(
                                    editableDoctors.map((x) => [x["userId"], x])
                                  ).values(),
                                ]}
                                onChange={(e, value) => {
                                  setEditableDoctors([
                                    ...fixedDoctor,
                                    ...value.filter(
                                      (option) =>
                                        fixedDoctor.indexOf(option) === -1
                                    ),
                                  ]);
                                }}
                                getOptionLabel={(option) => {
                                  return (
                                    option.firstName + " " + option.lastName
                                  );
                                }}
                                getOptionSelected={(option, value) =>
                                  option.userId === value.userId
                                }
                                renderOption={(option) => (
                                  <Box className={classes.optionsContainer}>
                                    <Box className={classes.optionAvatar}>
                                      <Avatar
                                        alt="profile"
                                        src={option.imageUrl}
                                      />
                                    </Box>
                                    <Box className={classes.optionText}>
                                      {`${option.firstName} ${option.lastName}`}
                                    </Box>
                                  </Box>
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Doctors"
                                    variant="outlined"
                                  />
                                )}
                                renderTags={(tagValue, getTagProps) => {
                                  return tagValue.map((option, index) => (
                                    <DoctorChip
                                      key={index}
                                      variant="outlined"
                                      {...getTagProps({ index })}
                                      label={`${option.firstName} ${option.lastName}`}
                                      url={option.imageUrl}
                                      color={
                                        option.userId ===
                                          singleCase.createdByUser.userId ||
                                        option.userId === getUserInfo().id
                                          ? "primary"
                                          : "default"
                                      }
                                      deleteIcon={
                                        option.userId ===
                                          singleCase.createdByUser.userId ||
                                        option.userId === getUserInfo().id ? (
                                          <CheckIcon />
                                        ) : (
                                          <RemoveIcon />
                                        )
                                      }
                                    />
                                  ));
                                }}
                                classes={{ inputRoot: classes.input }}
                              />
                            </Grid>

                            <Grid
                              item
                              xs={12}
                              sm={12}
                              md={3}
                              lg={3}
                              xl={3}
                              alignItems="center"
                            >
                              <Box
                                display="flex"
                                justifyContent="space-evenly"
                                gap={1.5}
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  disableElevation
                                  onClick={() => setOpenDoctorList(false)}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  disableElevation
                                  onClick={handleDoctorsList}
                                >
                                  Update
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                      {selectedDept &&
                        selectedDept.OrganizationType ===
                          ORGANIZATION_TYPES.HEALTHCARE_PROVIDER &&
                        selectedDept.HealthcareOrgname && (
                          <>
                            <Box pt={2}>
                              <Typography classes={{ root: classes.hcLabel }}>
                                Healthcare Organisation:
                              </Typography>
                              <Typography>
                                {selectedDept.HealthcareOrgname}
                              </Typography>
                            </Box>
                          </>
                        )}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    mb={2}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      disableElevation
                      startIcon={<SwapHorizIcon />}
                      className={classes.switchCaseButton}
                      onClick={() => setOpenCasesList(true)}
                    >
                      Switch Case
                    </Button>
                    <Typography className={classes.caseNumber}>
                      Case #{singleCase.id}
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    {[
                      {
                        label: "Age",
                        value: singleCase.externalReport?.attributes?.age
                          ? `${singleCase.externalReport.attributes.age} Years`
                          : "-",
                      },
                      {
                        label: "Gender",
                        value:
                          singleCase.externalReport?.attributes?.gender ||
                          singleCase.Patient?.gender ||
                          "-",
                      },
                      {
                        label: "Date of Birth",
                        value: singleCase.externalReport?.attributes
                          ?.time_of_birth
                          ? new Date(
                              singleCase.externalReport.attributes.time_of_birth
                            ).toLocaleDateString()
                          : singleCase.Patient?.dob || "-",
                      },
                      {
                        label: "Height",
                        value: singleCase.externalReport?.attributes
                          ?.height_in_cms
                          ? `${singleCase.externalReport.attributes.height_in_cms} cm`
                          : "-",
                      },
                      {
                        label: "Weight",
                        value: singleCase.externalReport?.attributes?.weight_in_kg
                          ? `${singleCase.externalReport.attributes.weight_in_kg} Kg`
                          : "-",
                      },
                      {
                        label: "BMI",
                        value:
                          bmiFor(
                            singleCase.externalReport?.attributes?.weight_in_kg,
                            singleCase.externalReport?.attributes?.height_in_cms
                          ) ?? "-",
                      },
                    ].map((item) => (
                      <Grid item xs={6} key={item.label}>
                        <Typography className={classes.descLabel}>
                          {item.label}
                        </Typography>
                        <Typography className={classes.descText}>
                          {item.value}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Box>
            <Box className={classes.addNoteHeadingContainer}>
              <Box className={classes.toggleButtonsContainer}>
                <ToggleButtonGroup
                  value={sortCategory}
                  onChange={(e, value) => setSortCategory(value)}
                  exclusive
                  size="small"
                >
                  {role === ROLES.ROLE_DOCTOR && (
                    <ToggleButton
                      className={classes.sortButton}
                      classes={{ selected: classes.selected, label: classes.sortButtonLabel }}
                      value="healthInformation"
                    >
                      <FavoriteBorderIcon className={classes.tabIcon} />
                      Health Information
                    </ToggleButton>
                  )}

                  {role === ROLES.ROLE_DOCTOR && (
                    <ToggleButton
                      className={classes.prescriptionButton}
                      classes={{ selected: classes.selected, label: classes.sortButtonLabel }}
                      value="prescription"
                    >
                      <AssignmentOutlinedIcon className={classes.tabIcon} />
                      Prescription
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              </Box>
            </Box>
            {sortCategory === "healthInformation" && singleCase?.Patient && (
              <Box className={classes.healthInformationContainer}>
                <HealthInformationTabs
                  valueHealthHistory={valueHealthHistory}
                  handleChangeHealthHistory={handleChangeHealthHistory}
                  patient={singleCase.Patient}
                  caseId={singleCase.id}
                />
              </Box>
            )}

            {sortCategory === "prescription" && (
              <Box className={classes.prescriptionContainer}>
                {!isOpenPrescriptionForm ? (
                  <PrescriptionList
                    caseId={singleCase.id}
                    onOpenPrescriptionForm={() => {
                      setSelectedPrescription(null);
                      setIsOpenPrescriptionForm(true);
                      setFormKey(Date.now());
                    }}
                    patientId={
                      singleCase.Patient.patientId ||
                      singleCase.Patient.mobile?.replace(/^\+91/, "")
                    }
                    onSelectPrescription={(prescriptionData) => {
                      setSelectedPrescription(prescriptionData);
                      setIsGoBackClicked(false);
                      setFormKey(Date.now());
                      setIsOpenPrescriptionForm(true);
                    }}
                    PatientInfo={singleCase.Patient}
                  />
                ) : (
                  <PrescriptionForm
                    key={formKey}
                    caseId={singleCase.id}
                    patient={singleCase.Patient}
                    externalReport={singleCase.externalReport}
                    patientPhone={singleCase.Patient?.mobile?.replace(
                      /^\+91/,
                      ""
                    )}
                    isOpenPrescriptionForm={isOpenPrescriptionForm}
                    setIsGoBackClicked={setIsGoBackClicked}
                    prescription={selectedPrescription}
                    onClosePrescriptionForm={() => {
                      setIsOpenPrescriptionForm(false);
                      setSelectedPrescription(null);
                    }}
                  />
                )}
              </Box>
            )}
          </>
        )}
      </Box>

      <CaseCloseConfirmationDialog
        title="Close Case"
        message="You are about to close an urgent case."
        open={openCloseCaseConfirmation}
        onCancel={handleConfirmationClose}
        onConfirm={handleCloseCase}
      />

      <MessageDialog
        title="Sorry!"
        message="Please re-open case to access."
        open={openCloseCaseMessage}
        setOpen={setOpenCloseCaseMessage}
      />

      {singleCase && (
        <PatientCasesDialog
          open={openCasesList}
          onClose={() => setOpenCasesList(false)}
          onSelect={handleCaseSelect}
          patientId={singleCase.PatientId || singleCase.Patient?.id}
          patientName={
            singleCase.externalReport?.attributes?.name ??
            (singleCase.Patient
              ? `${singleCase.Patient.firstname} ${singleCase.Patient.lastname}`
              : "")
          }
          currentCaseId={singleCase.id}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    selectedDept: state.ayucareUsers.selectedDept,
    singleCase: state.cases.case,
    caseStatus: state.cases.caseStatus,
    doctorsList: state.cases.doctors,
    casePriorities: state.cases.casePriorities,
    logItems: state.cases.logItems,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getCaseById: (id) => {
      return dispatch(getCaseByIdRequest(id)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    updateCase: (id, data) => {
      return dispatch(updateCaseRequest(id, data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    updateCaseDoctors: (caseId, data) => {
      return dispatch(updateCaseDoctorsRequest(caseId, data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getCaseStatus: (data) => {
      return dispatch(getCaseStatusRequest(data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getCaseStatusForDoctor: (data) => {
      return dispatch(getCaseStatusForDoctorRequest(data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getDoctorsOfOrg: () => {
      return dispatch(getOrgDoctorsRequest()).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getLogItemsByCaseId: (id, type) => {
      return dispatch(getLogItemsByCaseIdRequest(id, type)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    addLogItem: (caseId, data) => {
      return dispatch(addLogItemRequest(caseId, data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getSignedUrl: (data) => {
      return dispatch(getSignedUrlRequest(data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    uploadToS3: (url, file) => {
      return dispatch(uploadToS3Request(url, file)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Case);
